<template>
  <div class="report-page">
    <ReportHeaderTab />

    <!-- Page Header -->
    <div class="page-header">
      <div>
        <h5 class="page-title">Sales Report</h5>
        <p class="page-sub">Track individual sales transactions by date, store and payment type</p>
      </div>

      <div class="filters-wrap">

        <!-- Date range -->
        <div class="filter-group">
          <div class="filter-label">From</div>
          <div class="date-input-wrap">
            <font-awesome-icon icon="calendar" class="date-icon" />
            <Datepicker v-model="tableData.start_date" :enableTimePicker="false" class="date-picker" />
          </div>
        </div>

        <div class="filter-sep">—</div>

        <div class="filter-group">
          <div class="filter-label">To</div>
          <div class="date-input-wrap">
            <font-awesome-icon icon="calendar" class="date-icon" />
            <Datepicker v-model="tableData.end_date" :enableTimePicker="false" class="date-picker" />
          </div>
        </div>

        <!-- Store -->
        <div class="filter-group">
          <div class="filter-label">Store</div>
          <Multiselect
            v-model="tableData.store"
            label="name"
            :options="getStore"
            placeholder="All Stores"
            :searchable="true"
            :create-option="false"
            :close-on-select="true"
            class="filter-multiselect"
          />
        </div>

        <!-- Payment type -->
        <div class="filter-group">
          <div class="filter-label">Payment</div>
          <select v-model="tableData.payment_type" class="filter-select">
            <option v-for="pay in paymentTypes" :key="pay.id" :value="pay.value">
              {{ pay.name }}
            </option>
          </select>
        </div>

        <!-- Search -->
        <div class="filter-group">
          <div class="filter-label">Search</div>
          <div class="search-wrap">
            <font-awesome-icon icon="search" class="search-icon" />
            <input
              type="text"
              class="search-input"
              placeholder="Search sales..."
              v-model="query"
            />
          </div>
        </div>

        <!-- Apply -->
        <button class="btn-apply" :disabled="crudLoading" @click="fetchData()">
          <font-awesome-icon :icon="crudLoading ? 'spinner' : 'filter'" :spin="crudLoading" />
          {{ crudLoading ? 'Applying...' : 'Apply' }}
        </button>

        <!-- Export -->
        <div class="dropdown">
          <button class="btn-outline" @click="exportOpen = !exportOpen">
            <font-awesome-icon icon="download" /> Export
          </button>
          <ul class="dropdown-menu" :class="{ show: exportOpen }">
            <li>
              <a class="dropdown-item" href="#" @click.prevent="openExportModal('pdf')">
                <font-awesome-icon icon="file-pdf" class="text-danger me-2" /> PDF
              </a>
            </li>
            <li>
              <a class="dropdown-item" href="#" @click.prevent="openExportModal('csv')">
                <font-awesome-icon icon="file-csv" class="text-success me-2" /> CSV
              </a>
            </li>
          </ul>
        </div>

      </div>
    </div>

    <!-- Summary card -->
    <div class="summary-grid" v-if="details.length > 0 && !loading">
      <div class="summary-card">
        <div class="summary-icon summary-icon--blue">
          <font-awesome-icon icon="receipt" />
        </div>
        <div>
          <div class="summary-label">Total Sales</div>
          <div class="summary-value">{{ pagination.total }}</div>
        </div>
      </div>
      <div class="summary-card">
        <div class="summary-icon summary-icon--green">
          <font-awesome-icon icon="coins" />
        </div>
        <div>
          <div class="summary-label">Total Amount</div>
          <div class="summary-value">
            {{ company?.currency?.symbol }}{{ $filters.formatMoney(total_amount) }}
          </div>
        </div>
      </div>
    </div>

    <!-- Data card -->
    <div class="data-card">

      <!-- Loading -->
      <div v-if="loading" class="state-block">
        <div class="spinner-border text-primary" style="width:3rem;height:3rem;" role="status"></div>
        <p class="mt-3 text-muted-cell">Loading sales data...</p>
      </div>

      <!-- Empty -->
      <div v-else-if="details.length === 0" class="state-block">
        <div class="state-icon">
          <font-awesome-icon icon="receipt" />
        </div>
        <p class="state-title">No sales found</p>
        <p class="state-sub">
          {{ query ? 'No results match your search.' : 'Try adjusting your filters or date range and click Apply.' }}
        </p>
      </div>

      <template v-else>

        <!-- Toolbar -->
        <div class="table-toolbar">
          <span class="table-count">
            Showing <strong>{{ pagination.from }}–{{ pagination.to }}</strong>
            of <strong>{{ pagination.total }}</strong> sales
          </span>
          <div class="toolbar-right">
            <label class="table-count">Rows:</label>
            <select v-model.number="tableData.length" @change="tableData.page = 1; fetchData()" class="rows-select">
              <option v-for="n in [5,10,20,50,100,200,500,1000]" :key="n" :value="n">{{ n }}</option>
            </select>
          </div>
        </div>

        <!-- Table -->
        <div class="table-wrap">
          <table class="data-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Sales Number</th>
                <th>Date</th>
                <th>Time</th>
                <th>Customer</th>
                <th class="text-right">Total ({{ company?.currency?.symbol }})</th>
                <th>Payment Type</th>
                <th class="col-actions">Action</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(sale, index) in details" :key="sale.id">

                <td class="text-muted-cell">{{ pagination.from + index }}</td>

                <td>
                  <span class="batch-code">{{ sale.batch_code }}</span>
                </td>

                <td class="text-muted-cell" style="white-space:nowrap">{{ sale.date }}</td>

                <td class="text-muted-cell">{{ timeConvert(sale.time) }}</td>

                <td>
                  <span class="customer-name">{{ sale.customer?.customer_name || '—' }}</span>
                </td>

                <td class="text-right fw-bold">
                  {{ $filters.formatMoney(sale.total_amount) }}
                </td>

                <td>
                  <span class="payment-badge" :class="paymentClass(sale.payment_type)">
                    {{ formatPaymentType(sale.payment_type) }}
                  </span>
                </td>

                <td class="col-actions">
                  <div class="actions-cell">
                    <router-link
                      class="view-btn"
                      :to="isDirectPayment(sale.payment_type)
                        ? $url('/sales-receipt/' + sale.id)
                        : $url('/sales-receipt-credit/' + sale.id)"
                    >
                      <font-awesome-icon icon="eye" /> View
                    </router-link>

                    <Alert
                      :modalId="'singleModal'"
                      @alertModal="handleAction(sale.id)"
                      :modalText="'Are you sure you want to reverse this sale?'"
                      :action="'alertModal'"
                      title="Reverse Sale"
                    />
                  </div>
                </td>

              </tr>
            </tbody>

            <tfoot>
              <tr class="grand-total-row">
                <td colspan="5" class="grand-label">Grand Total</td>
                <td class="text-right fw-bold">{{ $filters.formatMoney(total_amount) }}</td>
                <td colspan="2"></td>
              </tr>
            </tfoot>
          </table>
        </div>

        <!-- Pagination -->
        <div v-if="pagination.lastPage > 1" class="pagination-bar">
          <span class="pagination-info">Showing {{ pagination.from }}–{{ pagination.to }} of {{ pagination.total }}</span>
          <div class="pagination-controls">
            <button class="pg-btn" :disabled="pagination.currentPage <= 1" @click="tableData.page = 1; fetchData()">«</button>
            <button class="pg-btn" :disabled="pagination.currentPage <= 1" @click="tableData.page = pagination.currentPage - 1; fetchData()">‹ Prev</button>
            <span class="pg-pages">
              <button v-for="p in pageRange" :key="p" class="pg-num" :class="{ 'pg-num--active': p === pagination.currentPage }" @click="tableData.page = p; fetchData()">{{ p }}</button>
            </span>
            <button class="pg-btn" :disabled="pagination.currentPage >= pagination.lastPage" @click="tableData.page = pagination.currentPage + 1; fetchData()">Next ›</button>
            <button class="pg-btn" :disabled="pagination.currentPage >= pagination.lastPage" @click="tableData.page = pagination.lastPage; fetchData()">»</button>
          </div>
        </div>

      </template>
    </div>

  </div>

  <!-- ── Export Column Picker ──────────────────────────────────────────── -->
  <transition name="fade">
    <div v-if="showExportModal" class="col-picker-backdrop" @click.self="showExportModal = false">
      <div class="col-picker-modal">
        <div class="col-picker-header">
          <div>
            <h3 class="col-picker-title">Select Columns</h3>
            <p class="col-picker-sub">Choose what to include in your {{ pendingExportFmt.toUpperCase() }} export</p>
          </div>
          <button class="col-picker-close" @click="showExportModal = false">
            <font-awesome-icon icon="times" />
          </button>
        </div>
        <div class="col-picker-actions">
          <button class="col-picker-link" @click="selectedExportCols = exportColumnDefs.map(c => c.key)">Select all</button>
          <span class="col-picker-sep">·</span>
          <button class="col-picker-link" @click="selectedExportCols = []">Deselect all</button>
          <span class="col-picker-count">{{ selectedExportCols.length }} of {{ exportColumnDefs.length }} selected</span>
        </div>
        <div class="col-picker-grid">
          <label v-for="col in exportColumnDefs" :key="col.key" class="col-picker-item">
            <input type="checkbox" :value="col.key" v-model="selectedExportCols" class="col-picker-check" />
            <span class="col-picker-label">{{ col.label }}</span>
          </label>
        </div>
        <div class="col-picker-footer">
          <button class="btn-ghost-cp" @click="showExportModal = false">Cancel</button>
          <button class="btn-primary-cp"
            :disabled="selectedExportCols.length === 0 || exportLoading"
            @click="doExport()">
            <font-awesome-icon
              :icon="exportLoading ? 'spinner' : pendingExportFmt === 'pdf' ? 'file-pdf' : 'file-csv'"
              :spin="exportLoading" />
            Export {{ pendingExportFmt.toUpperCase() }}
          </button>
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
import axios from 'axios';
import { useToast } from 'vue-toastification';
import { reusableMixin } from '../../../lib/reusableMixin';
import Multiselect from '@vueform/multiselect';
import ReportHeaderTab from './components/reportheadertab.vue';
import Alert from '../../../components/AlertModal.vue';
import jsPDF from 'jspdf';
import 'jspdf-autotable';

export default {
  name: 'SalesReport',

  components: { ReportHeaderTab, Multiselect, Alert },

  mixins: [reusableMixin],

  computed: {
    pageRange() {
      if (!this.pagination.lastPage) return [];
      const cur = this.pagination.currentPage, last = this.pagination.lastPage;
      const range = [];
      for (let i = Math.max(1, cur - 2); i <= Math.min(last, cur + 2); i++) range.push(i);
      return range;
    },
  },

  data() {
    return {
      print_content:     [],
      headerArrayColumn: ['S/N', 'Sales Number', 'Date', 'Time', 'Customer', 'Total Amount', 'Payment Type'],
      footerRowArray:    [],

      details:     [],
      paymentTypes:[],
      loading:     false,
      crudLoading: false,
      exportOpen:  false,
      exportLoading: false,
      showExportModal: false,
      pendingExportFmt: 'csv',
      selectedExportCols: [],
      exportColumnDefs: [
        { key: 'batch_code',   label: 'Sales Number', pdfWidth: 130 },
        { key: 'date',         label: 'Date',         pdfWidth: 90 },
        { key: 'time',         label: 'Time',         pdfWidth: 90 },
        { key: 'customer',     label: 'Customer',     pdfWidth: 231 },
        { key: 'total_amount', label: 'Total',        pdfWidth: 120 },
        { key: 'payment_type', label: 'Payment Type', pdfWidth: 100 },
      ],
      query:       null,

      tableData: { page: 1,
        store:        'All',
        payment_type: 'All',
        start_date:   new Date(),
        end_date:     new Date(),
        draw:         0,
        length:       5,
        search:       '',
        column:       0,
        dir:          'desc',
        status:       [0, 1, 2, 3],
        query:        '',
      },

      pagination: {
        lastPage: '', currentPage: '', total: '',
        lastPageUrl: '', nextPageUrl: '', prevPageUrl: '',
        from: 1, to: '',
      },

      total_amount: 0,
      toast: useToast(),
    };
  },

  methods: {
    // ── Helpers ─────────────────────────────────────────────────────────
    isDirectPayment(type) {
      const t = (type ?? '').toLowerCase();
      return t === 'cash' || t === 'card';
    },

    formatPaymentType(type) {
      if (!type) return '—';
      const t = type.toLowerCase();
      if (t === 'part' || t === 'split') return `${this.capitalize(t)} Payment`;
      return this.capitalize(type);
    },

    capitalize(str) {
      return str.charAt(0).toUpperCase() + str.slice(1);
    },

    paymentClass(type) {
      const t = (type ?? '').toLowerCase();
      if (t === 'cash')  return 'payment-badge--cash';
      if (t === 'card')  return 'payment-badge--card';
      if (t === 'part')  return 'payment-badge--part';
      if (t === 'split') return 'payment-badge--split';
      return 'payment-badge--other';
    },

    timeConvert(time) {
      if (!time) return '—';
      const match = time.toString().match(/^([01]\d|2[0-3])(:)([0-5]\d)(:[0-5]\d)?$/) || [time];
      if (match.length > 1) {
        const parts = match.slice(1);
        parts[5] = +parts[0] < 12 ? 'AM' : 'PM';
        parts[0] = +parts[0] % 12 || 12;
        return parts.join('');
      }
      return time;
    },

    // ── Export ──────────────────────────────────────────────────────────
    openExportModal(format) {
      this.pendingExportFmt   = format;
      this.selectedExportCols = this.exportColumnDefs.map(c => c.key);
      this.showExportModal    = true;
      this.exportOpen         = false;
    },

    getRowValue(row, key) {
      switch (key) {
        case 'batch_code':   return row.batch_code || '—';
        case 'date':         return row.date || '—';
        case 'time':         return this.timeConvert(row.time);
        case 'customer':     return row.customer?.customer_name || '—';
        case 'total_amount': return this.$filters.formatMoney(row.total_amount);
        case 'payment_type': return this.formatPaymentType(row.payment_type);
        default:             return '—';
      }
    },

    async doExport() {
      if (!this.selectedExportCols.length) return;
      this.exportLoading   = true;
      this.showExportModal = false;
      try {
        const params = { ...this.tableData, page: 1, length: 10000 };
        const res    = await axios.get('/report/sales-report', { params });
        const rows   = Array.isArray(res.data?.data?.sales?.data) ? res.data.data.sales.data : [];
        const cols    = this.exportColumnDefs.filter(c => this.selectedExportCols.includes(c.key));
        const headers = cols.map(c => c.label);
        const body    = rows.map(row => cols.map(c => this.getRowValue(row, c.key)));
        const date    = new Date().toISOString().slice(0, 10);
        if (this.pendingExportFmt === 'csv') {
          const csv  = [headers, ...body]
            .map(r => r.map(v => `"${String(v).replace(/"/g, '""')}"`).join(','))
            .join('\n');
          const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' });
          const url  = URL.createObjectURL(blob);
          const a    = document.createElement('a');
          a.href = url; a.download = `sales-report-${date}.csv`; a.click();
          URL.revokeObjectURL(url);
        } else {
          const doc       = new jsPDF({ orientation: 'landscape', unit: 'pt', format: 'a4' });
          const usable    = 761;
          const totalW    = cols.reduce((s, c) => s + c.pdfWidth, 0);
          const scale     = usable / totalW;
          const colStyles = {};
          cols.forEach((c, i) => { colStyles[i] = { cellWidth: c.pdfWidth * scale }; });
          doc.setFontSize(18); doc.setFont('helvetica', 'bold'); doc.setTextColor(26, 43, 69);
          doc.text('Sales Report', 40, 46);
          doc.setFontSize(9); doc.setFont('helvetica', 'normal'); doc.setTextColor(122, 144, 176);
          doc.text(`Exported ${new Date().toLocaleDateString('en-GB')}   ·   ${rows.length} record(s)`, 40, 62);
          doc.autoTable({
            startY: 78, head: [headers], body,
            styles: { fontSize: 7.5, cellPadding: 3.5, textColor: [26, 43, 69], overflow: 'ellipsize' },
            headStyles: { fillColor: [0, 74, 173], textColor: 255, fontStyle: 'bold', fontSize: 7.5 },
            alternateRowStyles: { fillColor: [248, 250, 252] },
            columnStyles: colStyles, tableWidth: usable, margin: { left: 40, right: 40 },
            didDrawPage(data) {
              doc.setFontSize(8); doc.setTextColor(192, 202, 214);
              doc.text(`Page ${data.pageNumber}`, doc.internal.pageSize.width / 2, doc.internal.pageSize.height - 16, { align: 'center' });
            },
          });
          doc.save(`sales-report-${date}.pdf`);
        }
      } catch { this.toast.error('Export failed. Please try again.'); }
      finally { this.exportLoading = false; }
    },

    // ── Data ────────────────────────────────────────────────────────────
    grandTotal(items) {
      this.total_amount = items.reduce(
        (s, t) => parseFloat((parseFloat(s) + parseFloat(t.total_amount)).toFixed(2)), 0,
      );
      this.footerRowArray = ['', 'GRAND TOTAL', '', '', '', this.$filters.formatMoney(this.total_amount), ''];
    },

    dataToPrint(details) {
      this.print_content = details.map((s, idx) => ({
        sn:           idx + 1,
        sales_number: s.batch_code,
        date:         s.date,
        time:         s.time,
        customer:     s.customer?.customer_name ?? '—',
        total_amt:    this.$filters.formatMoney(s.total_amount),
        payment_type: this.formatPaymentType(s.payment_type),
      }));
    },

    fetchData(url = '/report/sales-report') {
      this.loading     = true;
      this.crudLoading = true;
      this.tableData.query = this.query;
      this.tableData.draw++;

      axios.get(url, { params: this.tableData })
        .then(({ data }) => {
          this.details = data.data.sales.data;
          this.grandTotal(data.data.sales2);
          this.dataToPrint(this.details);
          this.configPagination(data.data.sales);
        })
        .catch(() => {
          this.toast.error('Failed to load sales data. Please try again.');
        })
        .finally(() => {
          this.loading     = false;
          this.crudLoading = false;
        });
    },

    handleAction(id) {
      axios.post(`/inventory/sales/reverse/${id}`)
        .then(({ data, status }) => {
          if (status === 200) {
            this.toast.success(data.message);
            this.fetchData();
          }
        })
        .catch(error => {
          this.toast.error(error?.response?.data?.message ?? 'Something went wrong.');
        });
    },

    configPagination(data) {
      this.pagination.lastPage    = data.last_page;
      this.pagination.currentPage = data.current_page;
      this.pagination.total       = data.total;
      this.pagination.lastPageUrl = data.last_page_url;
      this.pagination.nextPageUrl = data.next_page_url;
      this.pagination.prevPageUrl = data.prev_page_url;
      this.pagination.from        = data.from;
      this.pagination.to          = data.to;
    },
  },

  watch: {
    query() { this.fetchData(); },
  },

  created() {
    this.fetchData();
    this.allStore();
    this.allPaymentTypes();
    this.getCompany();
  },
};
</script>

<style scoped>
/* ── Page ────────────────────────────────────────────────────────────── */
.report-page { display:flex; flex-direction:column; gap:18px; }

/* ── Header ──────────────────────────────────────────────────────────── */
.page-header  { display:flex; align-items:flex-start; justify-content:space-between; flex-wrap:wrap; gap:14px; }
.page-title   { font-size:20px; font-weight:700; color:#1a2b45; margin:0 0 3px; }
.page-sub     { font-size:13px; color:#7a90b0; margin:0; }

/* ── Filters ─────────────────────────────────────────────────────────── */
.filters-wrap    { display:flex; align-items:flex-end; flex-wrap:wrap; gap:10px; }
.filter-group    { display:flex; flex-direction:column; gap:4px; }
.filter-label    { font-size:11px; font-weight:600; text-transform:uppercase; letter-spacing:.05em; color:#7a90b0; }
.filter-sep      { color:#b0bdd0; font-size:16px; margin-bottom:4px; }
.filter-select   { padding:8px 12px; border:1px solid #dde5f0; border-radius:8px; font-size:13px; color:#1a2b45; background:#fff; outline:none; cursor:pointer; min-width:140px; }
.filter-multiselect { min-width:150px; }

.date-input-wrap { position:relative; display:flex; align-items:center; }
.date-icon       { position:absolute; left:10px; color:#7a90b0; font-size:12px; pointer-events:none; z-index:1; }
.date-picker     { background:none; border:none; padding-left:28px; }

.search-wrap  { position:relative; display:flex; align-items:center; }
.search-icon  { position:absolute; left:11px; color:#7a90b0; font-size:13px; pointer-events:none; }
.search-input { padding:8px 12px 8px 33px; border:1px solid #dde5f0; border-radius:8px; font-size:13px; color:#1a2b45; background:#fff; width:180px; outline:none; transition:border-color .15s; }
.search-input:focus { border-color:#004AAD; }

.btn-apply { display:inline-flex; align-items:center; gap:7px; background:#004AAD; color:#fff; font-size:13px; font-weight:600; padding:8px 16px; border-radius:8px; border:none; cursor:pointer; transition:background .15s; white-space:nowrap; }
.btn-apply:hover:not(:disabled) { background:#003b8e; }
.btn-apply:disabled { opacity:.55; cursor:not-allowed; }

.btn-outline { display:inline-flex; align-items:center; gap:7px; background:#fff; color:#1a2b45; border:1px solid #dde5f0; font-size:13px; font-weight:500; padding:8px 14px; border-radius:8px; cursor:pointer; transition:background .15s; white-space:nowrap; }
.btn-outline:hover { background:#f4f7fb; }

.dropdown      { position:relative; }
.dropdown-menu { position:absolute; top:calc(100% + 6px); right:0; background:#fff; border:1px solid #e4eaf2; border-radius:10px; box-shadow:0 8px 24px rgba(0,0,0,.1); padding:6px; min-width:160px; z-index:200; display:none; }
.dropdown-menu.show { display:block; }
.dropdown-item { display:flex; align-items:center; gap:8px; padding:8px 12px; font-size:13px; border-radius:7px; color:#1a2b45; cursor:pointer; text-decoration:none; }
.dropdown-item:hover { background:#f4f7fb; }
.me-2 { margin-right:8px; }

/* ── Summary ─────────────────────────────────────────────────────────── */
.summary-grid { display:grid; grid-template-columns:repeat(2,1fr); gap:14px; max-width:500px; }
.summary-card { background:#fff; border:1px solid #e4eaf2; border-radius:12px; padding:18px 20px; display:flex; align-items:center; gap:16px; }
.summary-icon { width:44px; height:44px; border-radius:12px; display:flex; align-items:center; justify-content:center; font-size:18px; flex-shrink:0; }
.summary-icon--blue  { background:rgba(0,74,173,.1); color:#004AAD; }
.summary-icon--green { background:#f0fdf4; color:#16a34a; }
.summary-label { font-size:12px; font-weight:600; text-transform:uppercase; letter-spacing:.05em; color:#7a90b0; margin-bottom:5px; }
.summary-value { font-size:20px; font-weight:700; color:#1a2b45; }

/* ── Data card ───────────────────────────────────────────────────────── */
.data-card { background:#fff; border:1px solid #e4eaf2; border-radius:12px; overflow:hidden; }

/* ── States ──────────────────────────────────────────────────────────── */
.state-block { display:flex; flex-direction:column; align-items:center; justify-content:center; padding:64px 24px; text-align:center; }
.state-icon  { width:60px; height:60px; border-radius:16px; background:rgba(0,74,173,.08); color:#004AAD; display:flex; align-items:center; justify-content:center; font-size:24px; margin-bottom:16px; }
.state-title { font-size:16px; font-weight:700; color:#1a2b45; margin:0 0 6px; }
.state-sub   { font-size:13px; color:#7a90b0; margin:0; }
.text-muted-cell { color:#4a5e78; }
.mt-3 { margin-top:12px; }

/* ── Table toolbar ───────────────────────────────────────────────────── */
.table-toolbar  { display:flex; align-items:center; justify-content:space-between; padding:14px 20px; border-bottom:1px solid #e4eaf2; flex-wrap:wrap; gap:10px; }
.table-count    { font-size:13px; color:#7a90b0; }
.table-count strong { color:#1a2b45; }
.toolbar-right  { display:flex; align-items:center; gap:8px; }
.rows-select    { border:1px solid #dde5f0; border-radius:7px; padding:5px 10px; font-size:13px; color:#1a2b45; background:#f8fafc; outline:none; cursor:pointer; }

/* ── Table ───────────────────────────────────────────────────────────── */
.table-wrap  { overflow-x:auto; }
.data-table  { width:100%; border-collapse:collapse; }
.data-table thead tr { background:#f8fafc; }
.data-table thead th { font-size:11.5px; font-weight:700; text-transform:uppercase; letter-spacing:.05em; color:#7a90b0; padding:12px 16px; border-bottom:1px solid #e4eaf2; white-space:nowrap; }
.data-table tbody tr { border-bottom:1px solid #f0f4f8; transition:background .12s; }
.data-table tbody tr:last-child { border-bottom:none; }
.data-table tbody tr:hover { background:#f8fafc; }
.data-table tbody td { padding:12px 16px; font-size:13px; vertical-align:middle; }
.text-right  { text-align:right; }
.fw-bold     { font-weight:700; }
.col-actions { width:120px; text-align:center; }

/* ── Cells ───────────────────────────────────────────────────────────── */
.batch-code    { font-family:monospace; font-size:12px; font-weight:600; color:#1a2b45; background:#f4f7fb; border:1px solid #e4eaf2; border-radius:6px; padding:2px 8px; }
.customer-name { font-size:13px; font-weight:500; color:#1a2b45; }

/* ── Payment badges ──────────────────────────────────────────────────── */
.payment-badge { display:inline-block; border-radius:6px; padding:3px 10px; font-size:12px; font-weight:600; border:1px solid transparent; white-space:nowrap; }
.payment-badge--cash  { background:#f0fdf4; color:#16a34a; border-color:#bbf7d0; }
.payment-badge--card  { background:#edf4ff; color:#004AAD; border-color:#c5d6f5; }
.payment-badge--part  { background:#fff7ed; color:#e08c00; border-color:#fed7aa; }
.payment-badge--split { background:#f5f3ff; color:#7c3aed; border-color:#ddd6fe; }
.payment-badge--other { background:#f4f7fb; color:#7a90b0; border-color:#e4eaf2; }

/* ── Action buttons ──────────────────────────────────────────────────── */
.actions-cell { display:flex; align-items:center; justify-content:center; gap:6px; }
.view-btn { display:inline-flex; align-items:center; gap:5px; background:#f4f7fb; color:#004AAD; border:1px solid #c5d6f5; border-radius:7px; padding:5px 10px; font-size:12px; font-weight:600; text-decoration:none; transition:all .15s; white-space:nowrap; }
.view-btn:hover { background:#e8f0fb; color:#003b8e; text-decoration:none; }

/* ── Grand total ─────────────────────────────────────────────────────── */
.grand-total-row { background:#f8fafc; border-top:2px solid #e4eaf2; }
.grand-total-row td { padding:13px 16px; font-size:13px; }
.grand-label { font-size:12px; font-weight:700; text-transform:uppercase; letter-spacing:.05em; color:#7a90b0; }

/* ── Footer ──────────────────────────────────────────────────────────── */
.table-footer { padding:14px 20px; border-top:1px solid #e4eaf2; display:flex; justify-content:flex-end; }
.pagination-bar      { display:flex; align-items:center; justify-content:space-between; padding:14px 20px; border-top:1px solid #e4eaf2; flex-wrap:wrap; gap:10px; }
.pagination-info     { font-size:13px; color:#7a90b0; }
.pagination-controls { display:flex; align-items:center; gap:4px; }
.pg-btn  { padding:6px 11px; border:1px solid #dde5f0; border-radius:7px; background:#fff; color:#1a2b45; font-size:13px; cursor:pointer; transition:all .12s; }
.pg-btn:hover:not(:disabled) { background:#f4f7fb; border-color:#c8d6e8; }
.pg-btn:disabled { opacity:.4; cursor:not-allowed; }
.pg-pages { display:flex; gap:3px; }
.pg-num  { min-width:34px; padding:6px 8px; border:1px solid #dde5f0; border-radius:7px; background:#fff; color:#1a2b45; font-size:13px; cursor:pointer; transition:all .12s; text-align:center; }
.pg-num:hover { background:#f4f7fb; border-color:#c8d6e8; }
.pg-num--active { background:#004AAD; border-color:#004AAD; color:#fff; font-weight:600; }

/* ── Responsive ──────────────────────────────────────────────────────── */
@media (max-width:768px) {
  .page-header, .filters-wrap { flex-direction:column; align-items:stretch; }
  .filter-sep   { display:none; }
  .search-input { width:100%; }
  .summary-grid { grid-template-columns:1fr; max-width:100%; }
  .filter-select, .filter-multiselect { width:100%; }
}

/* ── Column picker modal ──────────────────────────────────────────────── */
.col-picker-backdrop { position:fixed; inset:0; background:rgba(15,25,40,.45); z-index:1050; display:flex; align-items:center; justify-content:center; padding:20px; }
.col-picker-modal    { background:#fff; border-radius:14px; width:100%; max-width:480px; box-shadow:0 24px 60px rgba(0,0,0,.18); overflow:hidden; }
.col-picker-header   { display:flex; align-items:flex-start; justify-content:space-between; padding:20px 22px 14px; border-bottom:1px solid #f0f4f9; }
.col-picker-title    { font-size:15px; font-weight:700; color:#1a2b45; margin:0 0 3px; }
.col-picker-sub      { font-size:12px; color:#7a90b0; margin:0; }
.col-picker-close    { background:none; border:none; color:#7a90b0; font-size:15px; cursor:pointer; padding:4px; line-height:1; }
.col-picker-close:hover { color:#1a2b45; }
.col-picker-actions  { display:flex; align-items:center; gap:8px; padding:10px 22px; background:#f8fafc; font-size:12px; border-bottom:1px solid #f0f4f9; }
.col-picker-link     { background:none; border:none; color:#004AAD; font-size:12px; cursor:pointer; padding:0; font-weight:600; font-family:inherit; }
.col-picker-link:hover { text-decoration:underline; }
.col-picker-sep      { color:#c5d3e0; }
.col-picker-count    { margin-left:auto; color:#7a90b0; font-size:11.5px; }
.col-picker-grid     { display:grid; grid-template-columns:1fr 1fr; gap:2px; padding:14px 16px; max-height:280px; overflow-y:auto; }
.col-picker-item     { display:flex; align-items:center; gap:9px; padding:9px 10px; border-radius:8px; cursor:pointer; transition:background .1s; }
.col-picker-item:hover { background:#f4f7fb; }
.col-picker-check    { width:15px; height:15px; accent-color:#004AAD; cursor:pointer; flex-shrink:0; }
.col-picker-label    { font-size:13px; color:#1a2b45; user-select:none; }
.col-picker-footer   { display:flex; justify-content:flex-end; gap:8px; padding:14px 20px; border-top:1px solid #f0f4f9; }
.btn-ghost-cp   { display:inline-flex; align-items:center; gap:7px; background:#f4f7fb; color:#3d5068; font-size:13px; font-weight:600; padding:9px 18px; border-radius:8px; border:1px solid #e4eaf2; cursor:pointer; font-family:inherit; }
.btn-ghost-cp:hover { background:#e4eaf2; }
.btn-primary-cp { display:inline-flex; align-items:center; gap:7px; background:#004AAD; color:#fff; font-size:13px; font-weight:600; padding:9px 18px; border-radius:8px; border:none; cursor:pointer; font-family:inherit; }
.btn-primary-cp:hover:not(:disabled) { background:#003b8e; }
.btn-primary-cp:disabled { opacity:.45; cursor:not-allowed; }
.fade-enter-active, .fade-leave-active { transition:opacity .15s ease; }
.fade-enter, .fade-leave-to { opacity:0; }
</style>